import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastAnimation } from '../toastr.animation';
import { Alert, AlertType, PositiionType } from '../toastr.type';
import { ToastrService } from '../toastr.service';
import { IconsComponent } from '@sml/icons';
import { Subscription } from 'rxjs/internal/Subscription';

@Component({
  selector: 'sml-toastr',
  standalone: true,
  imports: [CommonModule, IconsComponent],
  templateUrl: './toastr.component.html',
  styleUrl: './toastr.component.scss',
  animations: [ToastAnimation],
})
export class ToastrComponent implements OnInit, OnDestroy {
  @Input() id = '';
  @Input() isGlobal = false;
  @Input() delay = 5000;
  @Input() queueAlerts = false;
  @Input() keepOnRouteChange = false;
  @Input() maxItems = 1;
  @Input() position: PositiionType = PositiionType.TopCenter;
  @Input() icon = '';
  public isClose = true;
  public alerts: Alert[] = [];
  private queue: Array<Alert> = [];
  private readonly timeOutTimer: Array<ReturnType<typeof setTimeout>> = [];
  private readonly subscription!: Subscription;

  constructor(
    private readonly alertService: ToastrService,
    private readonly cd: ChangeDetectorRef,
  ) { }

  private static generateGuid() {
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  }

  ngOnInit() {
    if (this.isGlobal) {
      this.alertService.subject.subscribe(alert => {
        alert.isVisible = false;
        this.isClose = alert.close;
        this.icon = alert.icon;
        if (!alert.message && !alert.title) {
          this.clear();
          return;
        }
        this.showAlert(alert);
      });
    }
  }

  onAlertRemoved() {
    if (this.queue?.length && this.maxItems > this.alerts.length) {
      const alert = this.queue.pop();
      if (alert) {
        alert.isVisible = true;
        if (this.isTopPosition) {
          this.alerts.unshift(alert);
        } else {
          this.alerts.push(alert);
        }
        if (this.delay > 0) {
          this.timeOutTimer.push(setTimeout(() => {
            this.removeAlert(alert);
          }, this.delay));
        }
      }
    }
  }

  public queueAlert(alert: Alert): string {
    if (!alert) {
      return '';
    }
    alert.id = alert.id ?? ToastrComponent.generateGuid();
    const delay = alert.delay ?? this.delay;
    if (this.canShowAlertImmediately()) {
      this.showAlertImmediately(alert, delay);
    } else if (this.queueAlerts) {
      this.queue.push(alert);
    } else {
      this.replaceOldestAlert(alert, delay);
    }
    return alert.id;
  }

  private canShowAlertImmediately(): boolean {
    return !this.maxItems || this.alerts.length < this.maxItems;
  }

  private showAlertImmediately(alert: Alert, delay: number): void {
    alert.isVisible = true;
    this.addAlertToList(alert);
    if (delay > 0) {
      this.timeOutTimer.push(setTimeout(() => {
        this.removeAlert(alert);
      }, delay));
    }
  }

  private replaceOldestAlert(alert: Alert, delay: number): void {
    this.removeAlert(this.alerts[0]);
    this.addAlertToList(alert);
    this.timeOutTimer.push(setTimeout(() => { alert.isVisible = true; }, 350));
    if (delay > 0) {
      this.timeOutTimer.push(setTimeout(() => {
        this.removeAlert(alert);
      }, delay));
    }
  }

  private addAlertToList(alert: Alert): void {
    if (this.isTopPosition) {
      this.alerts.unshift(alert);
    } else {
      this.alerts.push(alert);
    }
  }

  // shows the alert imediately
  public showAlert(alert: Alert): string {
    if (!alert) {
      return '';
    }
    this.position = alert.position;
    this.queueAlert(alert);
    this.cd.detectChanges()
    return alert.id;
  }

  public show(message: string, title: string, type: AlertType, delay = null, id = null): string {
    const alert = new Alert();
    alert.id = id ?? '';
    alert.message = message;
    alert.title = title;
    alert.type = type ?? AlertType.info;
    alert.delay = delay;
    return this.showAlert(alert);
  }

  public error(message: string, title: string, delay = null, id = null): string {
    return this.show(message, title, AlertType.error, delay, id);
  }

  public success(message: string, title: string, delay = null, id = null): string {
    return this.show(message, title, AlertType.success, delay, id);
  }

  public info(message: string, title: string, delay = null, id = null): string {
    return this.show(message, title, AlertType.info, delay, id);
  }

  public warning(message: string, title: string, delay = null, id = null): string {
    return this.show(message, title, AlertType.warning, delay, id);
  }

  public default(message: string, title: string, delay = null, id = null): string {
    return this.show(message, title, AlertType.default, delay, id);
  }

  public removeAlert(alert: Alert) {
    if (!alert) {
      return
    }
    const anyRemoved = this.alerts.find(x => x !== alert) || this.queue.find(x => x !== alert);
    this.alerts = this.alerts.filter(x => x !== alert);
    this.queue = this.queue.filter(x => x !== alert);
    
    this.cd.detectChanges()
    if (anyRemoved) {
      this.onAlertRemoved();
    }
  }

  public remove(...ids: string[]) {
    if (!ids?.length) {
      return
    }
    ids.forEach(id => {
      const anyRemoved = this.alerts.find(x => x.id !== id) || this.queue.find(x => x.id !== id);
      this.alerts = this.alerts.filter(x => x.id !== id);
      this.queue = this.queue.filter(x => x.id !== id);
      if (anyRemoved) {
        this.onAlertRemoved();
      }
    });
  }

  public removeNotIn(...ids: Array<string>) {
    if (!ids?.length) {
      return
    }
    const idsToRemove = this.alerts.filter(x => ids.indexOf(x.id) < 0).map(x => x.id);
    this.remove(...idsToRemove);
  }

  clear() {
    this.alerts = [];
    this.queue.splice(0, this.queue.length);
    if (this.timeOutTimer) {
      this.timeOutTimer.forEach(t => {
        clearTimeout(t);
      });
      this.timeOutTimer.length = 0;
    }
  }

  cssClass(alert: Alert) {
    if (!alert) {
      return;
    }
    switch (alert.type) {
      case AlertType.success:
        return 'smlToastr smlToastr-success';
      case AlertType.error:
        return 'smlToastr smlToastr-error';
      case AlertType.info:
        return 'smlToastr smlToastr-info';
      case AlertType.warning:
        return 'smlToastr smlToastr-warning';
      case AlertType.default:
        return 'smlToastr smlToastr-default';
    }
  }

  iconClass(type: AlertType) {
    if (type == null) {
      return;
    }
    switch (type) {
      case AlertType.success:
        return 'success-icon';
      case AlertType.error:
        return 'error-icon';
      case AlertType.info:
        return 'info-icon';
      case AlertType.warning:
        return 'warning-icon';
      case AlertType.default:
        return 'default-icon';
    }
  }

  public get windowPosition() {
    switch (this.position) {
      case PositiionType.TopLeft:
      case PositiionType.TopRight:
      case PositiionType.TopCenter:
        return 'top';
      default:
        return 'bottom';
    }
  }

  public get isTopPosition() {
    switch (this.position) {
      case PositiionType.TopLeft:
      case PositiionType.TopRight:
      case PositiionType.TopCenter:
        return true;
      default:
        return false;
    }
  }

  public get positionClass() {
    // return css class based on alert type
    switch (this.position) {
      case PositiionType.TopLeft:
        return 'smlToastr-top-left';
      case PositiionType.TopRight:
        return 'smlToastr-top-right';
      case PositiionType.BottomCenter:
        return 'smlToastr-bottom-center';
      case PositiionType.BottomLeft:
        return 'smlToastr-bottom-left';
      case PositiionType.BottomRight:
        return 'smlToastr-bottom-right';
      case PositiionType.Middle:
        return 'smlToastr-middle-center';
      default:
        return 'smlToastr-top-center';
    }
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
